import React from 'react';
import { useTranslation } from 'react-i18next';

const Epsdos = () => {
  const { t } = useTranslation('help');

  return (
    <div style={styles.container}>
      {/* Title */}
      <h2 style={styles.title}>
        {t('Epsdos.title', 'AI Denial-of-Service (DoS) Endpoint Simulators:')}
      </h2>

      {/* Purpose Description */}
      <div style={styles.purposeSection}>
        <p style={styles.paragraph}>
          <strong style={styles.strongText}>
            {t('Epsdos.purposeTitle', '1. Purpose')}
          </strong>
        </p>
        <p style={styles.paragraph}>
          {t(
            'Epsdos.descriptions.purpose',
            'A Denial-of-Service (DoS) test for a secure AI endpoint evaluates how an application handles resource exhaustion, oversized inputs, and high-velocity traffic without crashing the underlying AI agent'
          )}
        </p>
      </div>

      {/* Section (a) */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(a)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsdos.labels.endpoint', 'Target :- ')}
            </strong>
            <a 
              href="http://52.53.127.59:5000/chat" 
              style={styles.link} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              http://52.53.127.59:5000/chat
            </a>
            {' '}
            {t(
              'Epsdos.descriptions.endpoint',
              'A Denial of Service (DoS) target in the context of AI prompts refers to a vulnerability where an attacker manipulates an Artificial Intelligence (AI) or Large Language Model (LLM) API target to consume excessive computing resources, making the service unavailable to legitimate'
            )}
          </p>
        </div>
      </div>

      {/* Section (b) - Request Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(b)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsdos.labels.requestJson', 'Request Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'Epsdos.descriptions.requestJson',
              'The simulator accepts an application/json body. AUTH_KEY is required inside request_template and must match an authorized key. The target validates it on every request and returns HTTP 401 when it is missing or invalid.'
            )}
          </p>

          {/* Request JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"headers"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Content-Type"</span>: <span style={styles.jsonString}>"application/json"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data_type"</span>: <span style={styles.jsonString}>"json"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"request_template"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"AUTH_KEY"</span>: <span style={styles.jsonString}>"&lt;authorized-key&gt;"</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"messages"</span>: [
            </div>
            <div style={styles.indent3}>
              {'{'} <span style={styles.jsonKey}>"role"</span>: <span style={styles.jsonString}>"user"</span>, <span style={styles.jsonKey}>"content"</span>: <span style={styles.jsonString}>"{"{{PROMPT}}"}"</span> {'}'}
            </div>
            <div style={styles.indent2}>],</div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"max_tokens"</span>: <span style={styles.jsonNumber}>256</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"prompt_path"</span>: <span style={styles.jsonString}>"messages.0.content"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (c) - Response Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(c)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsdos.labels.responseJson', 'Response Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'Epsdos.descriptions.responseJson',
              "A valid secure-mode request is blocked by the simulator's abuse policy. The message field contains the refusal and blocked is true."
            )}
          </p>

          {/* Response JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"target"</span>: <span style={styles.jsonString}>"/chat"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"mode"</span>: <span style={styles.jsonString}>"secure"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"error"</span>: <span style={styles.jsonBoolean}>true</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"blocked"</span>: <span style={styles.jsonBoolean}>true</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"message"</span>: <span style={styles.jsonString}>"{"{{RESP}}"}"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (d) - Expected Test Results */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(d)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsdos.labels.expectedResults', 'Expected Test Results against this simulator:-')}
            </strong>{' '}
            {t(
              'Epsdos.descriptions.expectedResults',
              'Since this simulator simulates secure DoS protection, the expected readiness result scores should be 90% or more, with malicious or resource-exhaustion requests being blocked.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component Styles (Dark Theme)
const styles = {
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    padding: '24px',
    color: '#e0e0e0',
    backgroundColor: '#181a1f',
    borderRadius: '8px',
    fontFamily: 'Segoe UI, Roboto, sans-serif'
  },
  title: {
    margin: '0 0 20px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '20px'
  },
  purposeSection: {
    marginBottom: '15px'
  },
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  sectionLabel: {
    width: '32px',
    flexShrink: 0,
    fontWeight: 'bold',
    color: '#a0a0a0'
  },
  sectionContent: {
    flexGrow: 1
  },
  paragraph: {
    margin: '0 0 10px 0',
    lineHeight: '1.6',
    color: '#cccccc',
    fontSize: '14px'
  },
  strongText: {
    color: '#ffffff'
  },
  link: {
    color: '#4fa3d1',
    textDecoration: 'underline'
  },
  codeBlock: {
    backgroundColor: '#0d0e11',
    color: '#e0e0e0',
    padding: '16px 20px',
    margin: '12px 0',
    borderRadius: '6px',
    border: '1px solid #2a2d34',
    fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    overflowX: 'auto',
    whiteSpace: 'pre'
  },
  indent1: {
    paddingLeft: '20px'
  },
  indent2: {
    paddingLeft: '40px'
  },
  indent3: {
    paddingLeft: '60px'
  },
  jsonKey: {
    color: '#6fc2ef'
  },
  jsonString: {
    color: '#e59373'
  },
  jsonNumber: {
    color: '#b5cea8'
  },
  jsonBoolean: {
    color: '#569cd6'
  }
};

export default Epsdos;